import { useMsal } from "@azure/msal-react";
import ApiService from "./controller";
import {
    FeedbackV2,
    ConfigResponse,
    ChatV2response,
    ChatV2,
    ChatHistoryResponse,
    HistoryRecord,
    StatusChange,
    SimpleMessage,
    IngestedFile,
    FaqResponse,
    Metadata,
    MetadataPerPage,
    Jargon,
    PromptSchemas,
    RevisionInfo,
    InfoResponse,
    ReadinessResponse,
    GraphsResponse,
    Chunk,
    ChatData,
    SpeechRecognizeResponse,
    Voice,
    StreamResponseV2,
    InitResponse,
} from "./models";
import { useSecuritySetting, persistStore } from "../store/store";
import { PersistState } from "../store/types";
import { ReadyState } from "react-use-websocket";
import { isValidElement } from "react";
import type { ReactNode } from "react";

export async function chatApi(chat: ChatV2): Promise<ChatV2response> {
    return ApiService.post<ChatV2response>("/api/v2/chat", chat);
}

export async function* chatApiStream(
    chat: ChatV2,
): AsyncGenerator<StreamResponseV2, void, unknown> {
    const store = useSecuritySetting.getState();
    const token = await store.getAccessToken();

    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Accept-Encoding", "identity");
    if (token !== "") {
        headers.append("Authorization", `Bearer ${token}`);
    }

    const response = await fetch("/api/v2/chat_stream", {
        method: "POST",
        headers: headers,
        body: JSON.stringify(chat),
        Microsoft_ApplicationInsights_BypassAjaxInstrumentation: true,
    } as RequestInit & { Microsoft_ApplicationInsights_BypassAjaxInstrumentation: boolean });

    if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
    }

    if (!response.body) {
        throw new Error("Response body is null");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            // SSE frames are separated by double newlines
            let boundary: number;
            while ((boundary = buffer.indexOf("\n\n")) !== -1) {
                const frame = buffer.substring(0, boundary);
                buffer = buffer.substring(boundary + 2);

                if (!frame.trim()) continue;

                // Parse SSE lines: collect event name and concatenate data lines
                let eventName = "";
                const dataLines: string[] = [];
                for (const line of frame.split("\n")) {
                    if (line.startsWith("event:")) {
                        eventName = line.slice("event:".length).trim();
                    } else if (line.startsWith("data:")) {
                        dataLines.push(line.slice("data:".length).trim());
                    }
                }

                if (!eventName || dataLines.length === 0) continue;

                const dataStr = dataLines.join("");
                try {
                    const parsed = JSON.parse(dataStr);
                    // The SSE frame carries event name separately; merge into the packet shape
                    const jsonChunk: StreamResponseV2 = {
                        event: eventName as StreamResponseV2["event"],
                        data: parsed.data ?? parsed,
                    };
                    yield jsonChunk;
                } catch (e) {
                    console.error("Failed to parse SSE data:", dataStr, e);
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}

export async function sendEscalationMessageApi(requestId: string, message: string): Promise<void> {
    await ApiService.post(`/api/v2/escalation/${requestId}/message`, { message });
}

export async function getAllIngestedFiles(): Promise<IngestedFile[]> {
    return ApiService.get<IngestedFile[]>("/api/v2/ingest/files");
}

export async function getInitData(): Promise<InitResponse> {
    return ApiService.get<InitResponse>("/api/v2/config/init_setup");
}
export interface DocumentPaginatedResponse {
    data: IngestedFile[];
    total: number;
}

function buildExportUrl(
    basePath: string,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): string {
    const params: string[] = [];
    if (sortField && sortOrder) {
        params.push(`sort_field=${sortField}`);
        params.push(`sort_order=${sortOrder}`);
    }
    if (filterModel?.quickFilterValues && filterModel.quickFilterValues.length > 0) {
        params.push(`quick_filter=${encodeURIComponent(filterModel.quickFilterValues[0])}`);
    }
    if (filterModel?.items && filterModel.items.length > 0) {
        params.push(`filters=${encodeURIComponent(JSON.stringify(filterModel.items))}`);
    }
    return params.length > 0 ? `${basePath}?${params.join("&")}` : basePath;
}

function buildPaginatedUrl(
    basePath: string,
    limit: number,
    offset: number,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): string {
    let url = `${basePath}?limit=${limit}&offset=${offset}`;
    if (sortField && sortOrder) {
        url += `&sort_field=${sortField}&sort_order=${sortOrder}`;
    }
    if (
        filterModel?.quickFilterValues &&
        filterModel.quickFilterValues.length > 0
    ) {
        url += `&quick_filter=${encodeURIComponent(filterModel.quickFilterValues[0])}`;
    }
    const filters: any[] = [...(filterModel?.items || [])];
    if (filters.length > 0) {
        url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    }
    return url;
}

export async function fetchDocumentsPaginated(
    limit: number = 50,
    offset: number = 0,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): Promise<DocumentPaginatedResponse> {
    const url = buildPaginatedUrl(
        "/api/v2/ingest/available_files",
        limit,
        offset,
        sortField,
        sortOrder,
        filterModel,
    );
    return ApiService.get<DocumentPaginatedResponse>(url);
}
export async function exportAllDocuments(
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): Promise<IngestedFile[]> {
    const url = buildExportUrl("/api/v2/ingest/export", sortField, sortOrder, filterModel);
    return ApiService.get<IngestedFile[]>(url);
}

export async function fetchDocumentMetadata(): Promise<{
    [key: string]: string[];
}> {
    return ApiService.get<{ [key: string]: string[] }>(
        "/api/v2/ingest/metadata",
    );
}

export async function sendFeedbackApi(
    feedback: FeedbackV2,
): Promise<FeedbackV2> {
    return ApiService.post<FeedbackV2>("/api/v2/feedbacks/rating", feedback);
}

export async function getConfig(): Promise<ConfigResponse> {
    return retry(() => ApiService.get<ConfigResponse>("/api/v2/config/config"));
}

export async function getUserHistory(): Promise<
    Record<string, ChatHistoryResponse[]>
> {
    return ApiService.get<Record<string, ChatHistoryResponse[]>>(
        "/api/v2/chat/days",
    );
}

export async function getUserHistoryById(
    user_id: string,
): Promise<Record<string, ChatHistoryResponse[]>> {
    return ApiService.get<Record<string, ChatHistoryResponse[]>>(
        `/api/v2/chat/days?user_id=${user_id}`,
    );
}

export async function getChat(chat_id: string): Promise<HistoryRecord[]> {
    return ApiService.get<HistoryRecord[]>(`/api/v2/chat/history/${chat_id}`);
}

export async function changeHistory(
    change: StatusChange,
): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/chat/status_change", change);
}

export async function fetchFile(citation: string): Promise<Blob> {
    const response = await ApiService.getBlob(citation);
    return response; // this is the PDF blob
}

export async function downloadPdf(
    citation: string,
    filename: string,
): Promise<void> {
    const blob = await fetchFile(citation);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export function getCitationFilePath(citation: string): string {
    return `/api/v2/content/${citation}`;
}

export function useOAuthToken(): string {
    if (useSecuritySetting((state) => state.authRequired)) {
        const { accounts } = useMsal();
        if (!accounts[0]) {
            return "";
        }
        // Return a non-sensitive account identifier; the ID token must not be
        // used as a bearer credential — use getAccessToken() for API calls.
        return accounts[0].homeAccountId;
    }
    return "";
}

export function useName(): string {
    if (useSecuritySetting((state) => state.authRequired)) {
        const { accounts } = useMsal();
        return accounts[0].name as string;
    }
    return persistStore((state: PersistState) => state.userId.slice(0, 8));
}

export async function retry<T>(
    func: () => Promise<T>,
    delay = 1000, // Default value, type inference from the default
    maxRetries = 5, // Optional parameter to limit retries
): Promise<T> {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            console.debug("Calling function: ", func.name);
            return await func();
        } catch (e) {
            if (attempt < maxRetries - 1) {
                await new Promise((resolve) =>
                    setTimeout(resolve, delay * (attempt + 1)),
                );
            } else {
                throw e; // Rethrow the last error after max retries
            }
        }
    }
    throw new Error("Function did not return a value");
}

// Add ingestFile endpoint
export async function ingestFile(formData: FormData): Promise<any> {
    return ApiService.postForm<any>("/api/v2/ingest/upload", formData);
}

// Ingest with parameters: file, dates, force, class, metadata
export interface IngestParams {
    file: File;
    valid_from?: string;
    valid_to?: string;
    force?: boolean;
    only_file?: boolean;
    metadata?: Array<{ metadata_key: string; metadata_value: string }>;
    metadata_per_page?: Array<{
        page: string;
        metadata_key: string;
        metadata_value: string;
    }>;
}

export async function ingest(params: IngestParams): Promise<Response> {
    const {
        file,
        valid_from,
        valid_to,
        force,
        only_file,
        metadata,
        metadata_per_page,
    } = params;
    const formData = new FormData();
    formData.append("file", file);
    if (metadata) {
        formData.append("metadata", JSON.stringify(metadata));
    }
    if (metadata_per_page) {
        formData.append("metadata_per_page", JSON.stringify(metadata_per_page));
    }
    const search = new URLSearchParams();
    if (valid_from && valid_from != "") search.append("valid_from", valid_from);
    if (valid_to && valid_to != "") search.append("valid_to", valid_to);
    if (force) search.append("force", "true");
    if (only_file) search.append("only_file", "true");
    const query = search.toString();
    const queryString = query ? `?${query}` : "";
    const url = `/api/v2/ingest/upload${queryString}`;
    return ApiService.postFormGetResponse(url, formData);
}

export async function deleteFile(fileId: string): Promise<Response> {
    return ApiService.delete(`/api/v2/ingest/delete?file_id=${fileId}`);
}

export async function deleteAllDocuments(): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>("/api/v2/ingest");
}

export async function changeMetadata(
    file_id: string,
    metadata: Array<Metadata>,
    metadata_per_page: Array<MetadataPerPage>,
    valid_from: string | null,
    valid_to: string | null,
): Promise<SimpleMessage> {
    if (valid_from == "") {
        valid_from = null;
    }
    if (valid_to == "") {
        valid_to = null;
    }

    return ApiService.put<SimpleMessage>("/api/v2/ingest/change_metadata", {
        filename: file_id,
        metadata: metadata,
        metadata_per_page: metadata_per_page,
        valid_from: valid_from,
        valid_to: valid_to,
        append: false,
    });
}

export async function changeBulkMetadata(
    json_bulk: any,
): Promise<SimpleMessage> {
    try {
        json_bulk.forEach((item: any, idx: number) => {
            if (typeof item !== "object" || item === null) {
                throw new Error(`Item at index ${idx} is not an object.`);
            }
            if (!item.filename) {
                throw new Error(`Missing filename at index ${idx}.`);
            }
            if (item.metadata && !Array.isArray(item.metadata)) {
                throw new Error(`metadata must be an array at index ${idx}.`);
            }
        });

        console.log("Changing bulk metadata payload:", json_bulk);
        return ApiService.put<SimpleMessage>(
            "/api/v2/ingest/change_metadata",
            json_bulk,
        );
    } catch (err) {
        console.error("Failed to parse bulk metadata JSON:", err);
        throw err;
    }
}

export async function fetchFaq(): Promise<FaqResponse[]> {
    return ApiService.get<FaqResponse[]>("/api/v2/faqs");
}

export interface FaqPaginatedResponse {
    data: FaqResponse[];
    total: number;
}

export async function fetchFaqsPaginated(
    limit: number = 50,
    offset: number = 0,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): Promise<FaqPaginatedResponse> {
    const url = buildPaginatedUrl(
        "/api/v2/faqs",
        limit,
        offset,
        sortField,
        sortOrder,
        filterModel,
    );
    return ApiService.get<FaqPaginatedResponse>(url);
}

export async function fetchRevisions(): Promise<RevisionInfo[]> {
    return ApiService.get<RevisionInfo[]>("/api/v2/schemas/revisions");
}

export async function fetchSchema(id: string): Promise<PromptSchemas> {
    return ApiService.get<PromptSchemas>(`/api/v2/schemas/${id}`);
}

export async function updateSchema(
    id: string,
    data: PromptSchemas,
): Promise<PromptSchemas> {
    return ApiService.put<PromptSchemas>(`/api/v2/schemas/${id}`, data);
}

export async function setActiveSchema(id: string): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>(
        `/api/v2/schemas/set_current/${id}`,
        {},
    );
}

export async function editSchema(data: PromptSchemas): Promise<PromptSchemas> {
    return ApiService.put<PromptSchemas>(
        `/api/v2/schemas/${data.revision}`,
        data,
    );
}

export async function addSchema(data: PromptSchemas): Promise<PromptSchemas> {
    return ApiService.post<PromptSchemas>("/api/v2/schemas/add_revision", data);
}

export async function deleteFaq(faqId: string): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>(`/api/v2/faqs/${faqId}`);
}

export async function bulkDeleteFaqs(ids: string[]): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/faqs/bulk-delete", ids);
}

export async function deleteAllFaqs(): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>("/api/v2/faqs");
}

export async function exportAllFaqs(
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): Promise<FaqResponse[]> {
    const url = buildExportUrl("/api/v2/faqs/export", sortField, sortOrder, filterModel);
    return ApiService.get<FaqResponse[]>(url);
}

export async function fetchFaqMetadata(): Promise<{ [key: string]: string[] }> {
    return ApiService.get<{ [key: string]: string[] }>("/api/v2/faqs/metadata");
}

export async function createFaq(faq: {
    question: string;
    answer: string;
    translated_question?: string;
    translated_answer?: string;
    metadata: Array<Metadata>;
}): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/faqs", faq);
}

export async function bulkCreateFaqs(
    faqs: Array<{
        question: string;
        answer: string;
        translated_question?: string;
        translated_answer?: string;
        metadata: Array<Metadata>;
    }>,
): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/faqs/bulk-create", faqs);
}

export async function uploadFaqFiles(
    files: File[],
    validity: Array<[string | null, string | null]>,
): Promise<SimpleMessage> {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("validity", JSON.stringify(validity));
    return ApiService.postForm<SimpleMessage>("/api/v2/faqs/upload-faq", formData);
}

export async function updateFaq(
    id: string,
    faq: {
        question: string;
        answer: string;
        translated_question?: string;
        translated_answer?: string;
        metadata: Array<Metadata>;
        valid_from?: string | null;
        valid_to?: string | null;
    },
): Promise<SimpleMessage> {
    return ApiService.put<SimpleMessage>(`/api/v2/faqs/${id}`, faq);
}

export const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
};

export async function getJargonEntries(): Promise<Jargon[]> {
    return ApiService.get<Jargon[]>("/api/v2/jargon");
}

export interface JargonPaginatedResponse {
    data: Jargon[];
    total: number;
}

export async function fetchJargonPaginated(
    limit: number = 50,
    offset: number = 0,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): Promise<JargonPaginatedResponse> {
    const url = buildPaginatedUrl(
        "/api/v2/jargon",
        limit,
        offset,
        sortField,
        sortOrder,
        filterModel,
    );
    return ApiService.get<JargonPaginatedResponse>(url);
}

export async function createJargonEntries(
    entries: Jargon[],
): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>(
        "/api/v2/jargon/bulk-create",
        entries,
    );
}

export async function createSingleJargonEntry(
    entry: Jargon,
): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/jargon", entry);
}

export async function updateJargonEntry(entry: Jargon): Promise<SimpleMessage> {
    return ApiService.put<SimpleMessage>(`/api/v2/jargon/${entry.pk}`, entry);
}

export async function deleteJargonEntry(pk: string): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>(`/api/v2/jargon/${pk}`);
}

export async function bulkDeleteJargonEntries(
    ids: string[],
): Promise<SimpleMessage> {
    return ApiService.post<SimpleMessage>("/api/v2/jargon/bulk-delete", ids);
}

export async function deleteAllJargonEntries(): Promise<SimpleMessage> {
    return ApiService.delete<SimpleMessage>("/api/v2/jargon");
}

export async function exportAllJargon(
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): Promise<Jargon[]> {
    const url = buildExportUrl("/api/v2/jargon/export", sortField, sortOrder, filterModel);
    return ApiService.get<Jargon[]>(url);
}

export async function fetchVoices(): Promise<Voice[]> {
    const response = await ApiService.get<Voice[]>("/api/v2/speech/voices");
    return response;
}

function stripCitations(value: string): string {
    return value.replace(/\[.*?\]/g, "");
}

function extractTextFromNode(node: unknown): string {
    if (node == null) {
        return "";
    }

    if (typeof node === "string" || typeof node === "number") {
        return stripCitations(String(node));
    }

    if (Array.isArray(node)) {
        return node
            .map((item) => extractTextFromNode(item))
            .filter(Boolean)
            .join(" ");
    }

    if (isValidElement(node)) {
        // Ignore buttons (citations)
        if (node.type === "button") {
            return "";
        }
        return extractTextFromNode(
            (node.props as { children?: ReactNode })?.children,
        );
    }

    return "";
}

export async function generateSpeech(
    text: string | ReactNode,
    speed: number,
    voice: string,
): Promise<Blob> {
    const cleanedText =
        typeof text === "string"
            ? stripCitations(text)
            : extractTextFromNode(text);
    return ApiService.postBlob("/api/v2/speech/tts", {
        text: cleanedText,
        speed,
        voice,
    });
}

export async function fetchConfigJson(): Promise<Blob> {
    const response = await ApiService.getBlob("/api/v2/config/config_export");
    return response;
}

export async function importConfig(file: File): Promise<{ status: string; imported: Record<string, number> }> {
    const formData = new FormData();
    formData.append("file", file);
    return ApiService.postForm("/api/v2/config/import_db", formData);
}

export async function fetchDbExport(
    onProgress: (downloaded: number, total: number | null) => void,
    onPhase: (phase: "preparing" | "downloading") => void,
    selections?: { faqs?: boolean; documents?: boolean; jargon?: boolean; pdf_files?: boolean; config?: boolean },
): Promise<Blob> {
    const store = useSecuritySetting.getState();
    const token = await store.getAccessToken();
    const headers: Record<string, string> = {};
    if (token !== "") headers["Authorization"] = `Bearer ${token}`;

    const params = new URLSearchParams();
    if (selections) {
        for (const [key, value] of Object.entries(selections)) {
            params.set(key, String(value));
        }
    }
    const query = params.toString();

    onPhase("preparing");
    const response = await fetch(`/api/v2/config/export_db${query ? `?${query}` : ""}`, { headers });
    if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    onPhase("downloading");
    const contentLength = response.headers.get("X-Archive-Size")
    const total = contentLength ? parseInt(contentLength, 10) : null;

    if (!response.body) {
        return await response.blob();
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let downloaded = 0;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        downloaded += value.length;
        onProgress(downloaded, total);
    }

    return new Blob(chunks as BlobPart[], { type: "application/zip" });
}

export async function recognizeSpeech(
    file: File,
    language?: string,
    speech_function?: string,
): Promise<SpeechRecognizeResponse> {
    const formData = new FormData();
    formData.append("file", file);
    if (language) {
        formData.append("language", language);
    }
    if (speech_function) {
        formData.append("speech_function", speech_function);
    }
    const resp = await ApiService.postForm<any>("/api/v2/speech/stt", formData);
    return {
        output_text: resp.output_text || "",
        pk: resp.pk || "",
    };
}

export async function fetchAppInfo(): Promise<InfoResponse> {
    const response = await ApiService.get<InfoResponse>(
        "/api/v2/config/general_info",
    );
    return response;
}

export async function fetchHealthcheck(): Promise<ReadinessResponse> {
    const response = await ApiService.get<ReadinessResponse>("/api/readiness");
    return response;
}

export async function fetchGraphs(): Promise<GraphsResponse> {
    const response = await ApiService.get<GraphsResponse>(
        "/api/v2/data/get_graphs",
    );
    return response;
}

export interface ChunkPaginatedResponse {
    data: Chunk[];
    total: number;
}

export async function fetchChunks(
    limit: number,
    page: number,
    sortModel?: any[],
    filterModel?: any,
): Promise<ChunkPaginatedResponse> {
    const sortField = sortModel?.[0]?.field;
    const sortOrder = sortModel?.[0]?.sort;
    const url = buildPaginatedUrl(
        "/api/v2/data/chunk_search",
        limit,
        page * limit,
        sortField,
        sortOrder,
        filterModel,
    );
    return ApiService.get<ChunkPaginatedResponse>(url);
}

export interface ChatPaginatedResponse {
    data: ChatData[];
    total: number;
}

export async function fetchFileSchema(): Promise<Record<string, any>> {
    const response = await ApiService.get<Record<string, any>>(
        "/api/v2/schemas/load/file_schema",
    );
    return response;
}

export async function fetchChats(
    limit: number = 50,
    offset: number = 0,
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[]; quickFilterValues?: string[] },
): Promise<ChatPaginatedResponse> {
    const url = buildPaginatedUrl(
        "/api/v2/data/chats",
        limit,
        offset,
        sortField,
        sortOrder,
        filterModel,
    );
    return ApiService.get<ChatPaginatedResponse>(url);
}

export async function exportChatsCSV(
    sortField?: string,
    sortOrder?: "asc" | "desc" | null,
    filterModel?: { items: any[] },
): Promise<Blob> {
    let url = "/api/v2/data/chats/export/csv";
    const params: string[] = [];

    if (sortField && sortOrder) {
        params.push(`sort_field=${sortField}`);
        params.push(`sort_order=${sortOrder}`);
    }
    if (filterModel?.items && filterModel.items.length > 0) {
        const filters = JSON.stringify(filterModel.items);
        params.push(`filters=${encodeURIComponent(filters)}`);
    }

    if (params.length > 0) {
        url += `?${params.join("&")}`;
    }

    return await ApiService.getBlob(url);
}
